import { MAX_TAG_LENGTH } from "./lifecycle-studio/esp-templating/tag-bounds";

/**
 * Braze personalization references — `{{${attr}}}`,
 * `{{custom_attribute.${attr}}}`, `{% if ${attr} == … %}` — are a
 * Braze-proprietary extension to Liquid: `$` is not a legal character in a
 * Liquid variable expression, so liquidjs throws a `TokenizationError` on
 * first contact. Emails authored for Braze carry these refs (often invisibly,
 * e.g. inside Return Path `X-rptags`/`X-rpcampaign` tracking comments), and a
 * single one poisons the parse of the whole template — the personalization
 * preview falls back to raw HTML and nothing renders.
 *
 * Braze also permits an output tag nested inside a logic tag —
 * `{% if {{${first_name}}} != blank %}` — because Braze renders inner
 * `{{ … }}` outputs before evaluating the enclosing tag. Standard Liquid
 * forbids nested delimiters, so the same one-tag-poisons-everything failure
 * applies even after the `${…}` refs are rewritten. The nested form also
 * appears with no whitespace at all (`{%if{{${name}}}!=blank%}` — the inner
 * braces are the token separators as far as Braze is concerned), so the
 * unwrap re-inserts a space on any side that would otherwise fuse tokens.
 *
 * `normalizeBrazeAttributeRefs` rewrites each ref to the equivalent standard
 * Liquid lookup at preview-render time only; the stored email HTML is never
 * modified, so Braze-bound emails keep their tags intact for the ESP:
 *
 *   {{${country}}}                      → {{country}}
 *   {{custom_attribute.${membership}}}  → {{custom_attribute.membership}}
 *   {{custom_attribute.${Total Spend}}} → {{custom_attribute["Total Spend"]}}
 *   time_zone:${time_zone}              → time_zone:time_zone   (filter args)
 *   {% if {{${first_name}}} != blank %} → {% if first_name != blank %}
 *   {%if{{${name}}}!=blank%}            → {%if name !=blank%}
 *
 * Only text inside `{{ }}` / `{% %}` delimiters is rewritten — a `${…}` in
 * page JS/CSS, or a literal price prefix like `You invested ${{amount}}`,
 * passes through untouched — and `{% raw %}` blocks are skipped since Liquid
 * emits their content verbatim. Known limitations, both of which would
 * require real tokenization for forms that don't appear in practice: a
 * `${…}` inside a quoted Liquid string literal is also rewritten, and a
 * quoted `"{{ … }}"` literal inside a tag is also unwrapped.
 *
 * `repairMangledLiquid` is a separate, second-chance pass for templates whose
 * Liquid was mangled by an HTML/rich-text editor rather than authored for
 * Braze: smart quotes inside delimiters (`{{’now’|date:“%d%m%Y”}}`) and
 * HTML-entity-escaped operators (`{% if LBS_LOST &gt; 9 %}`). Both forms are
 * real code to the ESP but token errors to liquidjs. The repair is only
 * applied by the renderer after a normal parse has already failed, so a
 * healthy template that legitimately carries a curly apostrophe inside a
 * string literal (`default: 'It’s here'`) is never rewritten.
 */

// Matches a single Liquid delimiter span — a tag (`{% … %}`) or an output
// (`{{ … }}`). Non-greedy so adjacent spans on one line stay distinct. Both scans
// for a closer are bounded (see MAX_TAG_LENGTH): this runs on request-supplied
// HTML, and an unterminated `{%` would otherwise rescan to the end of the input
// from every start position. Behavior delta: a span whose closer is more than
// MAX_TAG_LENGTH away is no longer matched at all, so its Braze refs are left
// alone and the Liquid parse fails exactly as it does today for such input.
const LIQUID_DELIMITER_SPAN_RE = new RegExp(
  String.raw`\{%(?=[\s\S]{0,${MAX_TAG_LENGTH}}?%\})[\s\S]*?%\}|\{\{(?=[\s\S]{0,${MAX_TAG_LENGTH}}?\}\})[\s\S]*?\}\}`,
  "g",
);

// Same bound on the scan for `{% endraw %}`; a raw block longer than
// MAX_TAG_LENGTH is not recognized, so its content is normalized like any other.
const RAW_BLOCK_RE = new RegExp(
  String.raw`\{%-?\s*raw\s*-?%\}(?=[\s\S]{0,${MAX_TAG_LENGTH}}?\{%-?\s*endraw\s*-?%\})[\s\S]*?\{%-?\s*endraw\s*-?%\}`,
  "g",
);

// A Braze ref inside a delimiter span. The optional leading `.` captures the
// `custom_attribute.${name}` member form so a non-identifier name can swap
// the dot for bracket access. Braze attribute names may contain spaces.
const BRAZE_REF_RE = /(\.)?\$\{\s*([^}]*?)\s*\}/g;

// Names outside this set (spaces, punctuation) need bracket-lookup form.
const LIQUID_IDENTIFIER_RE = /^[A-Za-z_][A-Za-z0-9_]*$/;

// An output nested inside a tag span, unwrapped to its bare expression:
// `{% if {{first_name}} != blank %}` → `{% if first_name != blank %}`.
const NESTED_OUTPUT_RE = /\{\{\s*([\s\S]*?)\s*\}\}/g;

// Cheap detector for the nested-output form so templates carrying it without
// any `${…}` ref still take the normalization scan. `[^{}]` cannot cross a
// `%}` boundary (it excludes `}`), so a match is a `{{` inside a single tag.
const NESTED_OUTPUT_GUARD_RE = /\{%[^{}]*\{\{/;

// Rewrites every delimiter span outside `{% raw %}` blocks; raw content is
// emitted verbatim by Liquid so it must never be normalized or repaired.
function mapDelimiterSpans(
  template: string,
  rewriteSpan: (span: string) => string,
): string {
  const rawRanges: Array<[number, number]> = [];
  for (const match of template.matchAll(RAW_BLOCK_RE)) {
    const start = match.index ?? 0;
    rawRanges.push([start, start + match[0].length]);
  }

  return template.replace(
    LIQUID_DELIMITER_SPAN_RE,
    (span, offset: number): string => {
      const insideRaw = rawRanges.some(
        ([start, end]) => offset >= start && offset + span.length <= end,
      );
      return insideRaw ? span : rewriteSpan(span);
    },
  );
}

function rewriteBrazeRef(dot: string | undefined, name: string): string {
  if (LIQUID_IDENTIFIER_RE.test(name)) {
    return dot ? `.${name}` : name;
  }
  const escaped = name.replace(/\\/g, "\\\\").replace(/"/g, '\\"');
  return `["${escaped}"]`;
}

// Unwraps `{{ … }}` outputs nested inside a tag span. Braze treats the inner
// braces themselves as token separators, so tags are often written with no
// surrounding whitespace (`{%if{{name}}!=blank%}`); dropping the braces there
// would fuse tokens (`ifname`, `name!=`). Pad each side that abuts a
// non-space character so the unwrapped expression stays a distinct token.
function unwrapNestedOutputs(tagSpan: string): string {
  return tagSpan.replace(
    NESTED_OUTPUT_RE,
    (match, expression: string, offset: number, whole: string): string => {
      const before = whole[offset - 1];
      const after = whole[offset + match.length];
      const lead = before && !/\s/.test(before) ? " " : "";
      const trail = after && !/\s/.test(after) ? " " : "";
      return `${lead}${expression}${trail}`;
    },
  );
}

// Rewrites Braze `${…}` refs in a bare expression (no surrounding Liquid
// delimiters), e.g. `custom_attribute.${membership}` →
// `custom_attribute.membership`. Also used to sanitize LLM-returned variable
// names that keep the Braze wrapper.
export function normalizeBrazeRefsInExpression(expression: string): string {
  return expression.replace(
    BRAZE_REF_RE,
    (_ref, dot: string | undefined, name: string) => rewriteBrazeRef(dot, name),
  );
}

export function normalizeBrazeAttributeRefs(template: string): string {
  // Cheap checks skip the span scan for the overwhelming majority of
  // templates. `${` outside a delimiter (e.g. `${{amount}}`) still takes
  // the scan but comes out unchanged.
  if (!template.includes("${") && !NESTED_OUTPUT_GUARD_RE.test(template)) {
    return template;
  }

  return mapDelimiterSpans(template, (span) => {
    const rewritten = normalizeBrazeRefsInExpression(span);
    // Rewriting `${…}` refs first turns `{{${first_name}}}` into the
    // well-formed `{{first_name}}`, so the unwrap sees balanced braces.
    return rewritten.startsWith("{%")
      ? unwrapNestedOutputs(rewritten)
      : rewritten;
  });
}

// Every character/entity the repair rewrites, as a cheap pre-scan guard.
const MANGLED_MARKER_RE = /[‘’“”]|&(?:gt|lt|quot|nbsp|amp|#0*39);/;

const SMART_SINGLE_QUOTE_RE = /[‘’]/g;
const SMART_DOUBLE_QUOTE_RE = /[“”]/g;

// `&amp;` is decoded last so a double-escaped `&amp;gt;` only unwraps one
// level per repair pass instead of jumping straight to `>`.
const HTML_ENTITY_REPLACEMENTS: Array<[RegExp, string]> = [
  [/&gt;/g, ">"],
  [/&lt;/g, "<"],
  [/&quot;/g, '"'],
  [/&#0*39;/g, "'"],
  [/&nbsp;/g, " "],
  [/&amp;/g, "&"],
];

export function repairMangledLiquid(template: string): string {
  if (!MANGLED_MARKER_RE.test(template)) {
    return template;
  }

  return mapDelimiterSpans(template, (span) => {
    let repaired = span
      .replace(SMART_SINGLE_QUOTE_RE, "'")
      .replace(SMART_DOUBLE_QUOTE_RE, '"');
    for (const [entity, replacement] of HTML_ENTITY_REPLACEMENTS) {
      repaired = repaired.replace(entity, replacement);
    }
    return repaired;
  });
}
